import type { OrderCardDTO, OrderServiceLineDTO, CareOrderListItemChecklist } from '@/api/orders'
import { countBaselineTaggedChecklistItems, appendBaselineSummaryToken } from '@/api/orders'
import { classifyCareOrderCardBucket, classifyCareSidebarSubBucket } from '@/api/hmCareOrderTypes'
import {
  CARE_SERVICE_STATUS_ASSIGNED,
  CARE_SERVICE_STATUS_COMPLETED,
  CARE_SERVICE_STATUS_FU_ASSIGNED,
  CARE_SERVICE_STATUS_FU_COMPLETED,
  CARE_SERVICE_STATUS_FU_PENDING_RESCHEDULE,
  CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE,
  CARE_SERVICE_STATUS_PENDING_RESCHEDULE,
  CARE_SERVICE_STATUS_PENDING_SCHEDULE,
  careServiceStatusCodeToDisplayLabel,
  careServiceStatusDisplayLabel,
  careOrderListServiceStatusApiParam,
  mainServiceStatusIsAssignedOrBeyond,
} from '@/api/hmCareOrderTypes'
import { formatOrderScheduleLineDisplay } from '@/utils/orderScheduleDisplay'

export { formatOrderScheduleLineDisplay }

/**
 * 与 `HMCareOrders.vue` 订单行展示一致。凡渲染同一套 `OrderCardDTO` / `OrderServiceLineDTO` 的界面，
 * 应对 Follow-up 时间行、首选时间、主行待排期小图标等复用此模块，避免各页规则分叉。
 */
export function isFollowUpCareLine(line: OrderServiceLineDTO): boolean {
  return line.serviceName.trim().toLowerCase() === 'follow-up care'
}

/** 订单卡片是否含 Follow-up Care 行（有则主行彩色码可展示 IC） */
export function orderCardHasFollowUpOrder(order: OrderCardDTO): boolean {
  return order.services.some((s) => isFollowUpCareLine(s))
}

/** 主单 Pending Schedule / Pending Reschedule：仅主单，不应有 Follow-up Care */
export function orderStatusDisallowsFollowUpCare(serviceStatusLabel: string | undefined): boolean {
  const bucket = classifyCareSidebarSubBucket(serviceStatusLabel ?? '')
  return bucket === 'pend_sched' || bucket === 'pend_resched'
}

/** 状态名带 FU / Follow-up 细分：业务上必有跟进行（具体行仍以后端 services 为准） */
export function orderStatusIsFollowUpContext(serviceStatusLabel: string | undefined): boolean {
  const raw = (serviceStatusLabel ?? '').trim()
  if (!raw || raw === '—') return false
  const bucket = classifyCareSidebarSubBucket(raw)
  if (bucket === 'fu_pend_sched' || bucket === 'fu_pend_resched') return true
  const n = raw.toLowerCase()
  const k = n.replace(/[\s-]+/g, '_')
  if (k === 'fu_assigned' || k === 'follow_up_assigned') return true
  if (n === CARE_SERVICE_STATUS_FU_ASSIGNED.trim().toLowerCase()) return true
  if (n === CARE_SERVICE_STATUS_FU_COMPLETED.trim().toLowerCase()) return true
  if (k === 'fu_completed' || k === 'follow_up_completed') return true
  if (/\bfu[\s_-]*assigned\b/.test(n) || /\bfollow[\s-]*up[\s_-]*assigned\b/.test(n)) return true
  if (/\bfu[\s_-]*completed\b/.test(n) || /\bfollow[\s-]*up[\s_-]*completed\b/.test(n)) return true
  return false
}

function orderStatusLabelIsCompleted(serviceStatusLabel: string | undefined): boolean {
  const raw = (serviceStatusLabel ?? '').trim()
  if (!raw || raw === '—') return false
  const s = raw.toLowerCase()
  if (s.includes('incomplete') || s.includes('pending')) return false
  if (/\bcompleted\b/.test(s) || /\bcomplete\b/.test(s)) return true
  if (/\bfu[\s_-]*completed\b/.test(s) || /\bfollow[\s-]*up[\s_-]*completed\b/.test(s)) return true
  return false
}

function followUpLineShowsPendingAssignment(line: OrderServiceLineDTO): boolean {
  if (line.technicianRef?.trim()) return false
  const assign = (line.assignment ?? '').trim()
  return !assign || /pending\s+assignment/i.test(assign)
}

/**
 * 订单卡片页头 Service Status（八种状态；有 Follow-up Care 行时必须带 Follow-up 前缀）
 */
export function resolveOrderCardHeadServiceStatus(order: OrderCardDTO): string {
  const fuLine = order.services.find(isFollowUpCareLine)
  if (!fuLine) {
    const fromCode = careServiceStatusCodeToDisplayLabel(order.serviceStatusCode)
    if (fromCode) return fromCode
    const bucket = classifyCareSidebarSubBucket(order.status ?? '')
    if (bucket === 'pend_sched') return CARE_SERVICE_STATUS_PENDING_SCHEDULE
    if (bucket === 'pend_resched') return CARE_SERVICE_STATUS_PENDING_RESCHEDULE
    return careServiceStatusDisplayLabel(order.status)
  }

  const fromCode = careServiceStatusCodeToDisplayLabel(order.serviceStatusCode)
  if (
    fromCode === CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE ||
    fromCode === CARE_SERVICE_STATUS_FU_PENDING_RESCHEDULE ||
    fromCode === CARE_SERVICE_STATUS_FU_ASSIGNED ||
    fromCode === CARE_SERVICE_STATUS_FU_COMPLETED
  ) {
    return fromCode
  }

  const rawStatus = (order.status ?? '').trim()
  if (orderStatusIsFollowUpContext(rawStatus)) {
    return careServiceStatusDisplayLabel(rawStatus)
  }

  if (orderStatusLabelIsCompleted(rawStatus) && /follow[\s-]*up|fu[\s_-]/i.test(rawStatus)) {
    return CARE_SERVICE_STATUS_FU_COMPLETED
  }

  const fuSchedRaw = (fuLine.scheduledAt ?? '').trim().toLowerCase()
  if (/pending\s*reschedule/.test(fuSchedRaw)) return CARE_SERVICE_STATUS_FU_PENDING_RESCHEDULE
  if (/pending\s*schedule/.test(fuSchedRaw)) return CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE

  const bucket = classifyCareOrderCardBucket(order)
  if (bucket === 'fu_pend_sched') return CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE
  if (bucket === 'fu_pend_resched') return CARE_SERVICE_STATUS_FU_PENDING_RESCHEDULE

  if (
    lineHasConfirmedScheduleTime(fuLine, order) &&
    !followUpLineShowsPendingAssignment(fuLine)
  ) {
    return CARE_SERVICE_STATUS_FU_ASSIGNED
  }

  if (
    mainServiceStatusIsAssignedOrBeyond(order.status, order.serviceStatusCode) ||
    orderStatusLabelIsCompleted(rawStatus)
  ) {
    return CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE
  }

  return careServiceStatusDisplayLabel(order.status) || CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE
}

function orderIncludesFollowUpCare(services: OrderServiceLineDTO[]): boolean {
  return services.some(isFollowUpCareLine)
}

function isPendingScheduleOrRescheduleText(text: string): boolean {
  const t = text.trim()
  if (!t || t === '—') return false
  if (t === PENDING_SCHEDULE_TIME_LABEL || t === PENDING_RESCHEDULE_TIME_LABEL) return true
  return (
    /\bpending\s+(schedule|reschedule)\b/i.test(t) ||
    /\bfu[\s-]*pending\s+(schedule|reschedule)\b/i.test(t)
  )
}

/** 有跟进行时主单应已有排期，不得再显示 Pending Schedule / Pending Reschedule 占位 */
function mainLineDisallowsPendingPlaceholders(order: OrderCardDTO, line: OrderServiceLineDTO): boolean {
  return !isFollowUpCareLine(line) && orderIncludesFollowUpCare(order.services)
}

function resolveMainLineTimeWhenFollowUpExists(
  line: OrderServiceLineDTO,
  order: OrderCardDTO,
): string {
  const raw = (line.scheduledAt ?? '').trim()
  if (raw && raw !== '—' && !isPendingScheduleOrRescheduleText(raw)) {
    const formatted = formatOrderScheduleLineDisplay(raw)
    if (formatted !== '—' && !isPendingScheduleOrRescheduleText(formatted)) {
      if (/(\d{1,2}:\d{2})\s*(AM|PM)/i.test(formatted)) return formatted
    }
  }
  const svcDate = order.serviceDate?.trim()
  if (svcDate && !isPendingScheduleOrRescheduleText(svcDate)) {
    const formatted = formatOrderScheduleLineDisplay(svcDate)
    if (formatted !== '—') return formatted
  }
  if (raw && raw !== '—') {
    const formatted = formatOrderScheduleLineDisplay(raw)
    return formatted !== '—' ? formatted : raw
  }
  return '—'
}

export function orderPreferredTimesList(order: OrderCardDTO): string[] {
  const raw = order.preferredTimes
  if (!raw?.length) return []
  return raw.map((x) => String(x).trim()).filter(Boolean)
}

/** 仅当订单有 **多个**（≥2）首选时间：Follow-up 行显示「Pending Reschedule」+ `reschedule@2x`，悬停黄底列出全部首选时间。 */
export function followUpRowShowsPreferredRescheduleBlock(
  order: OrderCardDTO,
  line: OrderServiceLineDTO,
): boolean {
  if (!isFollowUpCareLine(line) || orderPreferredTimesList(order).length <= 1) return false
  const bucket = classifyCareSidebarSubBucket(order.status ?? '')
  return bucket === 'fu_pend_resched' || bucket === 'pend_resched'
}

/** 主单「Pending Reschedule」且多条首选：主服务行展示「Pending Reschedule」+ 悬停黄底首选时间（与 Follow-up 行一致） */
export function mainLineRowShowsPendingReschedulePreferredBlock(
  order: OrderCardDTO,
  line: OrderServiceLineDTO,
): boolean {
  if (isFollowUpCareLine(line)) return false
  if (orderPreferredTimesList(order).length <= 1) return false
  const os = String(order.status ?? '')
    .trim()
    .toLowerCase()
  const sched = String(line.scheduledAt ?? '')
    .trim()
    .toLowerCase()
  const statusSaysMainPr =
    os.includes('pending') &&
    os.includes('reschedule') &&
    !os.includes('fu-') &&
    !os.includes('follow-up')
  const lineSaysPendingReschedule = /\bpending\s+reschedule\b/i.test(sched)
  if (!(statusSaysMainPr || lineSaysPendingReschedule)) return false
  return true
}

function mainLineScheduledAtIsServiceCyclePlaceholder(
  line: OrderServiceLineDTO,
  order: OrderCardDTO,
): boolean {
  if (isFollowUpCareLine(line)) return false
  if (!isPendingAssignmentLabel(line.assignment) || line.technicianRef?.trim()) return false

  const cycleRaw = order.serviceDate?.trim()
  if (!cycleRaw) return false
  const cycleYmd = cycleRaw.match(/^(\d{4}-\d{2}-\d{2})/)?.[1]
  if (!cycleYmd) return false

  const raw = (line.scheduledAt ?? '').trim()
  if (!raw || raw === '—') return false
  return raw === cycleRaw || raw.startsWith(cycleYmd)
}

/** 排程页已确认时段：含日期 + 时刻；待排期且仅有偏好时间时不算已排期 */
export function lineHasConfirmedScheduleTime(line: OrderServiceLineDTO, order?: OrderCardDTO): boolean {
  let raw = (line.scheduledAt ?? '').trim()
  if (!raw || raw === '—') return false
  if (isFollowUpCareLine(line)) raw = raw.replace(/^(follow-up-|fu-)/i, '').trim()
  if (/\bpending\s+(schedule|reschedule)\b/i.test(raw)) return false
  if (/\bfu[\s-]*pending\s+(schedule|reschedule)\b/i.test(raw)) return false

  const hasClock = /(\d{1,2}:\d{2})\s*(AM|PM)/i.test(raw)
  const hasDate =
    /\d{4}-\d{2}-\d{2}/.test(raw) || /[A-Za-z]{3}\s+\d{1,2},?\s*\d{4}/.test(raw)
  if (!hasClock || !hasDate) return false

  if (line.canReschedule && !line.canSchedule) {
    const assign = (line.assignment ?? '').trim()
    if (assign && !/pending\s+assignment/i.test(assign)) return true
  }

  if (order) {
    if (mainLineScheduledAtIsServiceCyclePlaceholder(line, order)) return false

    const bucket = classifyCareSidebarSubBucket(order.status ?? '')
    if (isFollowUpCareLine(line)) {
      if (bucket === 'fu_pend_sched') return false
      if (bucket === 'fu_pend_resched') {
        /** 客户改期待改期：仍保留原预约时段 */
        return hasClock && hasDate
      }
    } else if (bucket === 'pend_resched') {
      /** Pending Reschedule：有原预约时段则视为已排期，可 Unassign */
      return hasClock && hasDate
    } else if (bucket === 'pend_sched') {
      /** 待排期主单：须已指派检查员才算已确认排期（偏好时间 / 周期日占位不算） */
      const assign = (line.assignment ?? '').trim()
      const hasAssignee =
        !!line.technicianRef?.trim() ||
        (!!assign && !isPendingAssignmentLabel(assign))
      if (!hasAssignee) return false
      if (mainLineScheduledAtIsServiceCyclePlaceholder(line, order)) return false
      if (/\bpending\s+schedule\b/i.test(raw)) return false
      return hasClock && hasDate
    }
  }
  return true
}

/** 排程 Assign 成功后：行上已有预约区间 */
function lineHasPostAssignBookedTime(
  line: OrderServiceLineDTO,
  order: OrderCardDTO,
  _lineIndex: number,
): boolean {
  return lineHasConfirmedScheduleTime(line, order)
}

/** 待排期卡片在 Assign 后升为 Assigned / FU-Assigned（Service Status + Reschedule 按钮） */
export function promoteLineToAssignedCard(
  card: OrderCardDTO,
  lineIndex: number,
  isFu: boolean,
): OrderCardDTO {
  const line = card.services[lineIndex]
  if (!line) return card
  const scheduledAt = formatOrderScheduleLineDisplay((line.scheduledAt ?? '').trim())
  const assign = (line.assignment ?? '').trim()
  const services = card.services.map((l, i) => {
    if (i !== lineIndex) return l
    return {
      ...l,
      scheduledAt: scheduledAt !== '—' ? scheduledAt : l.scheduledAt,
      canSchedule: false,
      canReschedule: true,
      assignment:
        assign && !/^pending\s+assignment$/i.test(assign) ? assign : l.assignment,
    }
  })
  return {
    ...card,
    status: isFu ? CARE_SERVICE_STATUS_FU_ASSIGNED : CARE_SERVICE_STATUS_ASSIGNED,
    serviceStatusCode: isFu ? 'fu_assigned' : 'assigned',
    services,
  }
}

export function promoteBookedScheduleOnPendingCard(card: OrderCardDTO): OrderCardDTO {
  let next = card
  const code = (card.serviceStatusCode ?? '').trim().toLowerCase()
  const bucket = classifyCareSidebarSubBucket(card.status ?? '')
  const statusRaw = (card.status ?? '').trim()

  const mainIdx = next.services.findIndex((l) => !isFollowUpCareLine(l))
  if (mainIdx >= 0) {
    const isMainPending =
      code === 'pending_schedule' ||
      statusRaw === CARE_SERVICE_STATUS_PENDING_SCHEDULE ||
      bucket === 'pend_sched'
    const main = next.services[mainIdx]!
    const mainHasAssignee =
      main.technicianRef?.trim() || !isPendingAssignmentLabel(main.assignment)
    if (
      isMainPending &&
      mainHasAssignee &&
      lineHasPostAssignBookedTime(main, next, mainIdx)
    ) {
      next = promoteLineToAssignedCard(next, mainIdx, false)
    }
  }

  const fuIdx = next.services.findIndex((l) => isFollowUpCareLine(l))
  if (fuIdx >= 0) {
    const isFuPending =
      code === 'fu_pending_schedule' ||
      statusRaw === CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE ||
      bucket === 'fu_pend_sched'
    const fu = next.services[fuIdx]!
    const fuHasAssignee = fu.technicianRef?.trim() || !isPendingAssignmentLabel(fu.assignment)
    if (
      isFuPending &&
      fuHasAssignee &&
      lineHasPostAssignBookedTime(fu, next, fuIdx)
    ) {
      next = promoteLineToAssignedCard(next, fuIdx, true)
    }
  }

  return next
}

export function isPendingAssignmentLabel(text: string | undefined): boolean {
  const s = (text ?? '').trim()
  if (!s) return true
  return /^pending\s+assignment$/i.test(s)
}

/**
 * 指派列是否展示「Pending / Assignment」双行占位。
 * Pending Reschedule 阶段改的是时段而非首次待派，避免与页头 Pending Reschedule 语义冲突。
 */
export function assignmentColumnShowsPendingAssignmentPlaceholder(
  order: OrderCardDTO,
  line: OrderServiceLineDTO,
): boolean {
  if (!isPendingAssignmentLabel(line.assignment)) return false
  if (line.technicianRef?.trim()) return false
  const bucket = classifyCareSidebarSubBucket(order.status ?? '')
  if (isFollowUpCareLine(line)) {
    return bucket === 'fu_pend_sched'
  }
  return bucket === 'pend_sched'
}

export const PENDING_SCHEDULE_TIME_LABEL = 'Pending Schedule'
export const PENDING_RESCHEDULE_TIME_LABEL = 'Pending Reschedule'

function pendingScheduleTimeLabel(_line?: OrderServiceLineDTO): string {
  return PENDING_SCHEDULE_TIME_LABEL
}

function pendingRescheduleTimeLabel(_line?: OrderServiceLineDTO): string {
  return PENDING_RESCHEDULE_TIME_LABEL
}

/**
 * 订单级状态决定的 Pending 占位（仅主单 Pending* 或 FU 待排期/待改期下的跟进行）。
 * 主单在「有跟进」订单上应用订单桶占位；跟进行无确定时段时仍可由行内 scheduledAt / can* 决定。
 */
function pendingPlaceholderLabelForLine(
  line: OrderServiceLineDTO,
  order: OrderCardDTO,
): string | null {
  if (lineHasConfirmedScheduleTime(line, order)) return null
  if (isFollowUpCareLine(line) && line.canReschedule && !line.canSchedule) return null
  const bucket = classifyCareSidebarSubBucket(order.status ?? '')
  if (bucket === 'pend_sched') {
    return isFollowUpCareLine(line) ? null : pendingScheduleTimeLabel(line)
  }
  if (bucket === 'pend_resched') {
    return isFollowUpCareLine(line) ? null : pendingRescheduleTimeLabel(line)
  }
  if (bucket === 'fu_pend_sched') {
    return isFollowUpCareLine(line) ? pendingScheduleTimeLabel(line) : null
  }
  if (bucket === 'fu_pend_resched') {
    return isFollowUpCareLine(line) ? pendingRescheduleTimeLabel(line) : null
  }
  return null
}

/** 主服务行：在「Pending Schedule」或尚未排期且可排期时，时间文案后显示小图标（与各子目录筛选无关） */
export function mainLineNeedsScheduleGlyph(line: OrderServiceLineDTO, order?: OrderCardDTO): boolean {
  if (isFollowUpCareLine(line)) return false
  if (order && mainLineDisallowsPendingPlaceholders(order, line)) return false
  if (lineHasConfirmedScheduleTime(line, order)) return false
  if (order && orderPreferredTimesList(order).length > 0) return true
  const s = (line.scheduledAt ?? '').trim()
  if (/\bpending\s+schedule\b/i.test(s) || /\bfu[\s-]*pending\s+schedule\b/i.test(s)) return true
  if (!s || s === '—') return !!(line.canSchedule || line.canReschedule)
  if (!line.canSchedule) return false
  return false
}

export function followUpCareNeedsScheduleGlyph(line: OrderServiceLineDTO, order?: OrderCardDTO): boolean {
  if (!isFollowUpCareLine(line)) return false
  if (lineHasConfirmedScheduleTime(line, order)) return false
  if (order && orderPreferredTimesList(order).length > 0) return true
  const raw = (line.scheduledAt ?? '').trim()
  const s = raw.replace(/^(follow-up-|fu-)/i, '').trim()
  if (/\bpending\s+schedule\b/i.test(s) || /\bfu[\s-]*pending\s+schedule\b/i.test(s)) return true
  if (!raw || raw === '—') return !!(line.canSchedule || line.canReschedule)
  return false
}

/** 主行或 Follow-up 行：默认分支里在时段文案后显示「首选时间」悬停小图标 */
export function lineShowsPreferredTimeScheduleGlyph(
  line: OrderServiceLineDTO,
  order?: OrderCardDTO,
): boolean {
  return (
    (mainLineNeedsScheduleGlyph(line, order) && !isFollowUpCareLine(line)) ||
    followUpCareNeedsScheduleGlyph(line, order)
  )
}

export function orderCardIsMainPendingSchedule(order: OrderCardDTO): boolean {
  if (orderStatusIsFollowUpContext(order.status)) return false
  const code = (order.serviceStatusCode ?? '').trim().toLowerCase()
  if (code === 'pending_schedule') return true
  return classifyCareSidebarSubBucket(order.status ?? '') === 'pend_sched'
}

/** 跟进行仍可首次排期（主单已指派、App 新增跟进等） */
export function orderHasSchedulableFollowUpLine(order: OrderCardDTO): boolean {
  return order.services.some((l) => isFollowUpCareLine(l) && l.canSchedule)
}

/** FU-Pending Schedule 左栏待排：跟进行视为未分配（与主单 Pending Schedule 对称） */
export function orderCardIsFuPendingSchedule(order: OrderCardDTO): boolean {
  const code = (order.serviceStatusCode ?? '').trim().toLowerCase()
  if (code === 'fu_pending_schedule') return true
  if (classifyCareSidebarSubBucket(order.status ?? '') === 'fu_pend_sched') return true
  if (classifyCareOrderCardBucket(order) === 'fu_pend_sched') return true
  const main = order.services.find((l) => !isFollowUpCareLine(l))
  return (
    !!main &&
    lineHasConfirmedScheduleTime(main, order) &&
    orderHasSchedulableFollowUpLine(order)
  )
}

export function formatSvcTimeDisplay(line: OrderServiceLineDTO, order?: OrderCardDTO): string {
  if (lineHasConfirmedScheduleTime(line, order)) {
    let raw = (line.scheduledAt ?? '').trim()
    if (isFollowUpCareLine(line)) raw = raw.replace(/^(follow-up-|fu-)/i, '')
    return formatOrderScheduleLineDisplay(raw)
  }

  if (order) {
    const byStatus = pendingPlaceholderLabelForLine(line, order)
    if (byStatus) return byStatus
    if (mainLineDisallowsPendingPlaceholders(order, line)) {
      return resolveMainLineTimeWhenFollowUpExists(line, order)
    }
  }

  if (order && orderPreferredTimesList(order).length > 0 && isFollowUpCareLine(line)) {
    const bucket = classifyCareSidebarSubBucket(order.status ?? '')
    if (bucket === 'pend_resched' || bucket === 'fu_pend_resched') {
      return pendingRescheduleTimeLabel(line)
    }
    return pendingScheduleTimeLabel(line)
  }

  let raw = (line.scheduledAt ?? '').trim()
  if (!raw || raw === '—') {
    if (order && mainLineDisallowsPendingPlaceholders(order, line)) {
      return resolveMainLineTimeWhenFollowUpExists(line, order)
    }
    if (line.canReschedule && !line.canSchedule) return pendingRescheduleTimeLabel(line)
    if (line.canSchedule || line.canReschedule) return pendingScheduleTimeLabel(line)
    return '—'
  }
  if (isFollowUpCareLine(line)) raw = raw.replace(/^(follow-up-|fu-)/i, '').trim()
  if (order && mainLineDisallowsPendingPlaceholders(order, line) && isPendingScheduleOrRescheduleText(raw)) {
    return resolveMainLineTimeWhenFollowUpExists(line, order)
  }
  if (/\bpending\s+schedule\b/i.test(raw) || /\bfu[\s-]*pending\s+schedule\b/i.test(raw)) {
    return pendingScheduleTimeLabel(line)
  }
  if (/\bpending\s+reschedule\b/i.test(raw)) return pendingRescheduleTimeLabel(line)
  if (order && mainLineScheduledAtIsServiceCyclePlaceholder(line, order)) {
    return pendingScheduleTimeLabel(line)
  }
  return formatOrderScheduleLineDisplay(raw)
}

/** 列表接口未带 followups 时，按订单 FU 待排期/待改期状态补全跟进行 */
export function buildSyntheticFollowUpCareLine(
  order: OrderCardDTO,
  mainLine: OrderServiceLineDTO,
  options?: {
    followUpId?: string
    scheduledAt?: string | null
    assignment?: string
  },
): OrderServiceLineDTO {
  const bucket = classifyCareSidebarSubBucket(order.status ?? '')
  const isFuPendingSchedule = bucket === 'fu_pend_sched'
  const isFuPendingReschedule = bucket === 'fu_pend_resched'

  let scheduledAt = (options?.scheduledAt ?? '').trim()
  if (!scheduledAt || scheduledAt === '—') {
    if (isFuPendingReschedule) scheduledAt = PENDING_RESCHEDULE_TIME_LABEL
    else if (isFuPendingSchedule) scheduledAt = PENDING_SCHEDULE_TIME_LABEL
    else if (order.serviceDate?.trim()) scheduledAt = order.serviceDate.trim()
    else scheduledAt = PENDING_SCHEDULE_TIME_LABEL
  }

  return {
    serviceName: 'Follow-up Care',
    productCode: options?.followUpId?.trim() || '—',
    scheduledAt,
    address: mainLine.address,
    assignment: options?.assignment?.trim() || 'Pending Assignment',
    price: '—',
    paymentMethod: mainLine.paymentMethod,
    canSchedule: isFuPendingSchedule,
    canReschedule: isFuPendingReschedule,
    canRefund: false,
    canMessage: true,
    canNote: true,
  }
}

/** Follow-up-Pending Schedule / Reschedule 等业务上必有跟进行；缺失时补一行 */
export function ensureFollowUpCareLineOnOrder(order: OrderCardDTO): OrderCardDTO {
  if (!orderStatusIsFollowUpContext(order.status)) return order
  if (orderIncludesFollowUpCare(order.services)) return order
  const mainLine = order.services.find((line) => !isFollowUpCareLine(line)) ?? order.services[0]
  if (!mainLine) return order
  const fu = buildSyntheticFollowUpCareLine(order, mainLine)
  return { ...order, services: [...order.services, fu] }
}

/**
 * 跟进行 FU Pending Reschedule 但从未指派且无已确认时段（含主动排期后 Unassign 被接口误标为改期）→ FU Pending Schedule。
 * 与主单 repairMisclassifiedMainPendingReschedule 同一规则：有原预约时段才保留 Pending Reschedule。
 */
function repairMisclassifiedFuPendingReschedule(card: OrderCardDTO): OrderCardDTO {
  if (classifyCareSidebarSubBucket(card.status ?? '') !== 'fu_pend_resched') return card

  const fuIdx = card.services.findIndex((l) => isFollowUpCareLine(l))
  if (fuIdx < 0) return card

  const fu = card.services[fuIdx]!
  if (fu.technicianRef?.trim()) return card
  if (!isPendingAssignmentLabel(fu.assignment)) return card
  if (lineHasConfirmedScheduleTime(fu, card)) return card

  const sched = (fu.scheduledAt ?? '').trim()
  if (
    sched &&
    sched !== '—' &&
    !isPendingScheduleOrRescheduleText(sched) &&
    /(\d{1,2}:\d{2})\s*(AM|PM)/i.test(sched) &&
    (/\d{4}-\d{2}-\d{2}/.test(sched) || /[A-Za-z]{3}\s+\d{1,2},?\s*\d{4}/.test(sched))
  ) {
    return card
  }

  const services = card.services.map((line, i) => {
    if (i !== fuIdx) return line
    return {
      ...line,
      scheduledAt: CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE,
      assignment: 'Pending Assignment',
      technicianRef: undefined,
      canSchedule: true,
      canReschedule: false,
    }
  })

  return {
    ...card,
    status: CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE,
    serviceStatusCode: 'fu_pending_schedule',
    services,
  }
}

/**
 * 主单仍为 Pending Assignment、且无已确认排期时，应为 Pending Schedule（首次待排），
 * 不应为 Pending Reschedule。接口 service_status 已标 pending_reschedule 时以接口为准，不降级。
 */
function repairMisclassifiedMainPendingReschedule(card: OrderCardDTO): OrderCardDTO {
  const apiCode = (card.serviceStatusCode ?? '').trim().toLowerCase()
  if (apiCode === 'pending_reschedule') return card
  if (orderStatusIsFollowUpContext(card.status)) return card
  if (classifyCareSidebarSubBucket(card.status ?? '') !== 'pend_resched') return card

  const mainLine = card.services.find((line) => !isFollowUpCareLine(line)) ?? card.services[0]
  if (!mainLine) return card
  if (lineHasConfirmedScheduleTime(mainLine, card)) return card
  if (mainLine.technicianRef?.trim()) return card
  if (!isPendingAssignmentLabel(mainLine.assignment)) return card

  const services = card.services.map((line) => {
    if (isFollowUpCareLine(line)) return line
    const sched = (line.scheduledAt ?? '').trim()
    const nextScheduledAt =
      !sched || sched === '—' || /\bpending\s+reschedule\b/i.test(sched)
        ? PENDING_SCHEDULE_TIME_LABEL
        : line.scheduledAt
    return {
      ...line,
      scheduledAt: nextScheduledAt,
      canSchedule: true,
      canReschedule: false,
      canNote: true,
    }
  })

  return {
    ...card,
    status: CARE_SERVICE_STATUS_PENDING_SCHEDULE,
    serviceStatusCode: 'pending_schedule',
    services,
  }
}

/**
 * 新建/未指派主单：接口或归一化偶发标为 Assigned，但指派仍为 Pending Assignment。
 * Service Status 应为 Pending Schedule，不应显示已排期（Assigned）。
 */
function repairMisclassifiedMainAssigned(card: OrderCardDTO): OrderCardDTO {
  if (orderStatusIsFollowUpContext(card.status)) return card

  const code = (card.serviceStatusCode ?? '').trim().toLowerCase()
  const statusRaw = (card.status ?? '').trim()
  const isAssigned =
    code === 'assigned' || statusRaw === CARE_SERVICE_STATUS_ASSIGNED
  if (!isAssigned) return card

  const mainIdx = card.services.findIndex((line) => !isFollowUpCareLine(line))
  if (mainIdx < 0) return card

  const mainLine = card.services[mainIdx]!
  if (mainLine.technicianRef?.trim()) return card
  if (!isPendingAssignmentLabel(mainLine.assignment)) return card

  const services = card.services.map((line, i) => {
    if (i !== mainIdx) return line
    return {
      ...line,
      canSchedule: true,
      canReschedule: false,
      canNote: true,
    }
  })

  return {
    ...card,
    status: CARE_SERVICE_STATUS_PENDING_SCHEDULE,
    serviceStatusCode: 'pending_schedule',
    services,
  }
}

/** 订单级已是 Pending Schedule，但主单行仍带旧时段/人员（Unassign 后接口残留） */
function repairMainPendingScheduleDisplay(card: OrderCardDTO): OrderCardDTO {
  if (orderStatusIsFollowUpContext(card.status)) return card
  const code = (card.serviceStatusCode ?? '').trim().toLowerCase()
  const statusRaw = (card.status ?? '').trim()
  const isMainPendingSched =
    code === 'pending_schedule' ||
    statusRaw === CARE_SERVICE_STATUS_PENDING_SCHEDULE ||
    classifyCareSidebarSubBucket(statusRaw) === 'pend_sched'
  if (!isMainPendingSched) return card

  const mainIdx = card.services.findIndex((l) => !isFollowUpCareLine(l))
  if (mainIdx < 0) return card

  const main = card.services[mainIdx]!
  if (lineHasPostAssignBookedTime(main, card, mainIdx)) {
    return promoteLineToAssignedCard(card, mainIdx, false)
  }

  const statusEmpty = !statusRaw || statusRaw === '—'
  const needsRepair =
    statusEmpty ||
    ((main.assignment ?? '').trim() !== '' &&
      !/^pending\s+assignment$/i.test((main.assignment ?? '').trim())) ||
    main.canSchedule === false
  if (!needsRepair) return card

  const services = card.services.map((line, i) => {
    if (i !== mainIdx) return line
    return {
      ...line,
      scheduledAt: PENDING_SCHEDULE_TIME_LABEL,
      assignment: 'Pending Assignment',
      technicianRef: undefined,
      canSchedule: true,
      canReschedule: false,
    }
  })
  return {
    ...card,
    status: CARE_SERVICE_STATUS_PENDING_SCHEDULE,
    serviceStatusCode: card.serviceStatusCode ?? 'pending_schedule',
    services,
  }
}

/** 订单级已是 Follow-up-Pending Schedule，但跟进行仍带旧时段/人员（Unassign 后接口残留） */
function repairFollowUpPendingScheduleDisplay(card: OrderCardDTO): OrderCardDTO {
  const code = (card.serviceStatusCode ?? '').trim().toLowerCase()
  const isFuPendingSched =
    code === 'fu_pending_schedule' ||
    classifyCareSidebarSubBucket(card.status ?? '') === 'fu_pend_sched'
  if (!isFuPendingSched) return card

  const fuIdx = card.services.findIndex((l) => isFollowUpCareLine(l))
  if (fuIdx < 0) return card

  const fu = card.services[fuIdx]!
  const fuHasAssignee =
    !!fu.technicianRef?.trim() || !isPendingAssignmentLabel(fu.assignment)
  const sched = (fu.scheduledAt ?? '').trim()
  const hasBookedSlotResidue =
    !!sched &&
    sched !== '—' &&
    !isPendingScheduleOrRescheduleText(sched) &&
    /(\d{1,2}:\d{2})\s*(AM|PM)/i.test(sched) &&
    (/\d{4}-\d{2}-\d{2}/.test(sched) || /[A-Za-z]{3}\s+\d{1,2},?\s*\d{4}/.test(sched))

  if (fuHasAssignee && lineHasPostAssignBookedTime(fu, card, fuIdx)) {
    return promoteLineToAssignedCard(card, fuIdx, true)
  }

  const needsRepair =
    !fuHasAssignee &&
    (hasBookedSlotResidue ||
      ((fu.assignment ?? '').trim() !== '' &&
        !/^pending\s+assignment$/i.test((fu.assignment ?? '').trim())) ||
      fu.canSchedule === false)

  if (!needsRepair) return card

  const services = card.services.map((line, i) => {
    if (i !== fuIdx) return line
    return {
      ...line,
      scheduledAt: CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE,
      assignment: 'Pending Assignment',
      technicianRef: undefined,
      canSchedule: true,
      canReschedule: false,
    }
  })
  return {
    ...card,
    status: CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE,
    serviceStatusCode: card.serviceStatusCode ?? 'fu_pending_schedule',
    services,
  }
}

/** 跟进行 Unassign 后接口仍为 FU-Assigned，但无人员/无有效预约 → 回到 Follow-up-Pending Schedule */
function repairFuAssignedAfterFollowUpUnassign(card: OrderCardDTO): OrderCardDTO {
  const code = (card.serviceStatusCode ?? '').trim().toLowerCase()
  const statusRaw = (card.status ?? '').trim()
  const isFuAssigned =
    code === 'fu_assigned' ||
    code === 'follow_up_assigned' ||
    statusRaw === CARE_SERVICE_STATUS_FU_ASSIGNED
  if (!isFuAssigned) return card

  const fuIdx = card.services.findIndex((l) => isFollowUpCareLine(l))
  if (fuIdx < 0) return card

  const fu = card.services[fuIdx]!
  if (fu.technicianRef?.trim()) return card
  if (!isPendingAssignmentLabel(fu.assignment)) return card

  const services = card.services.map((line, i) => {
    if (i !== fuIdx) return line
    return {
      ...line,
      scheduledAt: CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE,
      assignment: 'Pending Assignment',
      technicianRef: undefined,
      canSchedule: true,
      canReschedule: false,
    }
  })
  return {
    ...card,
    status: CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE,
    serviceStatusCode: 'fu_pending_schedule',
    services,
  }
}

/** 合并卡片误把跟进行排期写到主单时，挪回 Follow-up Care 行 */
function repairMisplacedFollowUpSchedule(card: OrderCardDTO): OrderCardDTO {
  if (!orderIncludesFollowUpCare(card.services)) return card
  const bucket = classifyCareSidebarSubBucket(card.status ?? '')
  const fuContext =
    orderStatusIsFollowUpContext(card.status) ||
    bucket === 'fu_pend_sched' ||
    bucket === 'fu_pend_resched'
  if (!fuContext) return card

  const fuIdx = card.services.findIndex((l) => isFollowUpCareLine(l))
  const mainIdx = card.services.findIndex((l) => !isFollowUpCareLine(l))
  if (fuIdx < 0 || mainIdx < 0) return card

  const fu = card.services[fuIdx]!
  const main = card.services[mainIdx]!
  const fuStillPending =
    fu.canSchedule ||
    isPendingScheduleOrRescheduleText(fu.scheduledAt ?? '') ||
    !lineHasConfirmedScheduleTime(fu, card)
  if (!fuStillPending) return card
  if (!lineHasConfirmedScheduleTime(main, card)) return card

  const svcDateRaw = card.serviceDate?.trim()
  if (!svcDateRaw) return card
  const svcDateFmt = formatOrderScheduleLineDisplay(svcDateRaw)
  const mainFmt = formatOrderScheduleLineDisplay(main.scheduledAt ?? '')
  if (mainFmt === svcDateFmt || mainFmt === '—') return card

  /** 主单自身已有预约区间（与跟进行无关）时勿改写主单时段 */
  if (
    lineHasConfirmedScheduleTime(main, card) &&
    !isPendingScheduleOrRescheduleText(main.scheduledAt ?? '')
  ) {
    return card
  }

  const services = card.services.map((line, i) => {
    if (i === fuIdx) {
      return {
        ...line,
        scheduledAt: main.scheduledAt,
        assignment: main.assignment,
        technicianRef: main.technicianRef,
        canSchedule: false,
        canReschedule: true,
      }
    }
    if (i === mainIdx) {
      return {
        ...line,
        scheduledAt: svcDateRaw,
        canSchedule: false,
      }
    }
    return line
  })
  return { ...card, services }
}

/** 合并卡（主单+跟进行）：列表/排程 patch 时锁定主单行展示（时段、人员、服务名/码） */
export function preserveMainLineOnMergedFollowUpCard(
  existing: OrderCardDTO,
  incoming: OrderCardDTO,
): OrderCardDTO {
  const existingMainIdx = existing.services.findIndex((l) => !isFollowUpCareLine(l))
  const existingFuIdx = existing.services.findIndex((l) => isFollowUpCareLine(l))
  if (existingMainIdx < 0) return incoming
  const prevMain = existing.services[existingMainIdx]
  if (!prevMain) return incoming

  const restoreMainOnto = (line: OrderServiceLineDTO): OrderServiceLineDTO => ({
    ...line,
    serviceName: prevMain.serviceName,
    productCode: prevMain.productCode,
    scheduledAt: prevMain.scheduledAt,
    assignment: prevMain.assignment,
    technicianRef: prevMain.technicianRef,
    canSchedule: prevMain.canSchedule,
    canReschedule: prevMain.canReschedule,
    canRefund: prevMain.canRefund ?? line.canRefund,
    canMessage: prevMain.canMessage ?? line.canMessage,
    canNote: prevMain.canNote ?? line.canNote,
    price: prevMain.price?.trim() && prevMain.price !== '—' ? prevMain.price : line.price,
    id: prevMain.id ?? line.id,
  })

  const incomingMainIdx = incoming.services.findIndex((l) => !isFollowUpCareLine(l))
  if (incomingMainIdx >= 0) {
    const services = incoming.services.map((line, i) =>
      i === incomingMainIdx ? restoreMainOnto(line) : line,
    )
    return { ...incoming, services }
  }

  if (existingFuIdx < 0) return incoming

  const incomingFuIdx = incoming.services.findIndex((l) => isFollowUpCareLine(l))
  const fuLine =
    incomingFuIdx >= 0
      ? incoming.services[incomingFuIdx]!
      : incoming.services.find((l) => isFollowUpCareLine(l)) ?? incoming.services[0]
  if (!fuLine) return incoming

  return { ...incoming, services: [restoreMainOnto(prevMain), fuLine] }
}

function orderCardHasMainAndFollowUpLines(order: OrderCardDTO): boolean {
  return (
    order.services.some((l) => isFollowUpCareLine(l)) &&
    order.services.some((l) => !isFollowUpCareLine(l))
  )
}

function syncOrderCardStatusWithFollowUpHead(card: OrderCardDTO): OrderCardDTO {
  if (!orderIncludesFollowUpCare(card.services)) return card
  const head = resolveOrderCardHeadServiceStatus(card)
  if (!head || head === '—' || head === (card.status ?? '').trim()) return card
  const apiCode = careOrderListServiceStatusApiParam(head)
  return {
    ...card,
    status: head,
    serviceStatusCode:
      apiCode && apiCode !== 'all' ? apiCode : card.serviceStatusCode,
  }
}

/**
 * 列表展示前归一化服务行（与后端 services 一致，仅剔除明显不合规组合）：
 * - Pending Schedule / Pending Reschedule：仅保留主单行
 * - Follow-up-Pending Schedule / Reschedule：无跟进行时补全；不因主单时段解析失败而剔除跟进行
 * - 其它状态（Assigned、Completed 等）：是否含跟进行完全以后端为准；有跟进行时主单须有确定时段
 */
export function normalizeOrderCardForDisplay(order: OrderCardDTO): OrderCardDTO | null {
  let card = repairMisclassifiedMainPendingReschedule(ensureFollowUpCareLineOnOrder(order))
  let services = card.services

  if (orderStatusDisallowsFollowUpCare(card.status)) {
    services = services.filter((line) => !isFollowUpCareLine(line))
  } else if (orderIncludesFollowUpCare(services)) {
    const mainLines = services.filter((line) => !isFollowUpCareLine(line))
    const mainHasConfirmedTime = mainLines.some((line) =>
      lineHasConfirmedScheduleTime(line, card),
    )
    if (!mainHasConfirmedTime && !orderStatusIsFollowUpContext(card.status)) {
      services = mainLines
    } else {
      services = services.map((line) => {
        if (isFollowUpCareLine(line)) return line
        const sched = (line.scheduledAt ?? '').trim()
        const pendingMain =
          !sched ||
          sched === '—' ||
          isPendingScheduleOrRescheduleText(sched)
        let nextScheduledAt = line.scheduledAt
        if (pendingMain) {
          const svcDate = card.serviceDate?.trim()
          if (svcDate && !isPendingScheduleOrRescheduleText(svcDate)) {
            nextScheduledAt = svcDate
          } else if (pendingMain && (sched === PENDING_SCHEDULE_TIME_LABEL || !sched || sched === '—')) {
            nextScheduledAt = '—'
          }
        }
        return {
          ...line,
          scheduledAt: nextScheduledAt,
          canSchedule: false,
        }
      })
    }
  }

  if (!services.length) return null
  card = services === card.services ? card : { ...card, services }
  if (classifyCareOrderCardBucket(card) === 'fu_pend_resched') {
    const mainIdx = card.services.findIndex((l) => !isFollowUpCareLine(l))
    if (mainIdx >= 0 && card.services[mainIdx]?.canNote === false) {
      const servicesWithNote = card.services.map((line, i) =>
        i === mainIdx ? { ...line, canNote: true } : line,
      )
      card = { ...card, services: servicesWithNote }
    }
  }
  return syncOrderCardStatusWithFollowUpHead(
    repairMisclassifiedMainAssigned(
      promoteBookedScheduleOnPendingCard(
        repairMisplacedFollowUpSchedule(
          repairFuAssignedAfterFollowUpUnassign(
            repairMisclassifiedFuPendingReschedule(
              repairFollowUpPendingScheduleDisplay(repairMainPendingScheduleDisplay(card)),
            ),
          ),
        ),
      ),
    ),
  )
}

export function normalizeOrderCardListForDisplay(orders: OrderCardDTO[]): OrderCardDTO[] {
  const out: OrderCardDTO[] = []
  for (const o of orders) {
    const normalized = normalizeOrderCardForDisplay(o)
    const base = normalized ?? (o.services.length > 0 ? o : null)
    if (base) out.push(base)
  }
  return out
}

/** 订单卡片时间区：Pending Schedule / Pending Reschedule 占位（SF Pro Regular 14/20 #999） */
export function isPendingTimePlaceholderDisplay(
  line: OrderServiceLineDTO,
  order?: OrderCardDTO,
): boolean {
  const t = formatSvcTimeDisplay(line, order).trim()
  return t === PENDING_SCHEDULE_TIME_LABEL || t === PENDING_RESCHEDULE_TIME_LABEL
}

/** @deprecated 使用 isPendingTimePlaceholderDisplay */
export function isPendingScheduleTimeDisplay(
  line: OrderServiceLineDTO,
  order?: OrderCardDTO,
): boolean {
  return isPendingTimePlaceholderDisplay(line, order)
}

/** 时段占位或首选时间提示：文案后 16×16 小图标（reschedule@2x） */
export function lineShowsServiceTimeStatusIcon(
  line: OrderServiceLineDTO,
  order?: OrderCardDTO,
): boolean {
  if (isPendingTimePlaceholderDisplay(line, order)) {
    const label = formatSvcTimeDisplay(line, order).trim()
    // Pending Schedule：无客户偏好时间不显示图标；Pending Reschedule 仍显示
    if (label === PENDING_SCHEDULE_TIME_LABEL) {
      return !!order && orderPreferredTimesList(order).length > 0
    }
    return true
  }
  return lineShowsPreferredTimeScheduleGlyph(line, order)
}

function linePreferredTimeTooltipEntries(
  order: OrderCardDTO,
  line: OrderServiceLineDTO,
): string[] {
  if (lineHasConfirmedScheduleTime(line, order)) return []
  const pref = orderPreferredTimesList(order)
  if (pref.length) return pref
  let slot = (line.scheduledAt ?? '').trim()
  if (isFollowUpCareLine(line)) {
    slot = slot.replace(/^(follow-up-|fu-)/i, '').trim()
  }
  if (
    slot &&
    slot !== '—' &&
    !/^pending\s*schedule$/i.test(slot) &&
    !/^pending\s*reschedule$/i.test(slot)
  ) {
    return [(line.scheduledAt ?? '').trim()]
  }
  return []
}

/** Pending Schedule 无首选时间：不显示图标；有首选时间才出图标+黄底悬浮。Pending Reschedule 仍出悬浮框 */
export function lineShowsPreferredTimeTooltip(
  line: OrderServiceLineDTO,
  order?: OrderCardDTO,
): boolean {
  if (!lineShowsServiceTimeStatusIcon(line, order)) return false
  if (isPendingTimePlaceholderDisplay(line, order)) {
    const label = formatSvcTimeDisplay(line, order).trim()
    if (label === PENDING_SCHEDULE_TIME_LABEL) {
      return !!order && orderPreferredTimesList(order).length > 0
    }
    return true
  }
  if (!order) return false
  return linePreferredTimeTooltipEntries(order, line).length > 0
}

/** 与 `HMCareOrders.vue` 主行彩色码拆分一致 */
export function productCodeTagParts(code: string): string[] {
  const raw = (code ?? '').trim()
  if (!raw || raw === '—') return []
  if (/[·•,|]/.test(raw) || raw.includes('/')) {
    return raw.split(/\s*(?:[·•]|,|\||\/)\s*/).map((s) => s.trim()).filter(Boolean)
  }
  const compact = raw.replace(/\s+/g, ' ')
  if (/^(?:[A-Za-z]{1,3}-\d+\s+)+[A-Za-z]{1,3}-\d+$/i.test(compact)) {
    return compact.split(/\s+/).map((s) => s.trim()).filter(Boolean)
  }
  return [raw]
}

/** 订单卡片彩色码不包含 B（B 仅在 Checklist and Follow-up 顶栏展示） */
export function productCodeTagPartsForOrderCard(code: string): string[] {
  return productCodeTagParts(code).filter((p) => !/^B-\d+$/i.test(p.trim()))
}

function serviceStatusIsPendingScheduleDisplay(status: string | undefined): boolean {
  const s = (status ?? '').trim().toLowerCase()
  if (!s || s.includes('reschedule')) return false
  return s.includes('pending') && s.includes('schedule')
}

function serviceStatusShouldOmitInspectionPToken(status: string | undefined): boolean {
  const s = (status ?? '').trim().toLowerCase()
  if (!s || serviceStatusIsPendingScheduleDisplay(status)) return false
  return /\bassigned\b/.test(s)
}

function stripPInspectionTokensFromJoined(code: string): string {
  const raw = (code ?? '').trim()
  if (!raw || raw === '—') return raw || '—'
  const parts = raw.split(/\s*·\s*/).map((x) => x.trim()).filter(Boolean)
  const rest = parts.filter((p) => !/^P-\d+$/i.test(p))
  return rest.length ? rest.join(' · ') : '—'
}

function inspectionProductCodeHasTokens(code: string): boolean {
  return productCodeTagParts(code).some((t) => /^[A-Za-z]{1,3}-\d+$/i.test(t.trim()))
}

/** 主服务行检验彩色码展示串（与 Checklist and Follow-up 顶栏一致，不含 B） */
export function mainLineInspectionProductCodeDisplay(
  order: OrderCardDTO,
  line: OrderServiceLineDTO,
): string {
  if (isFollowUpCareLine(line)) return line.productCode
  const tokens = productCodeTagPartsForOrderCard(line.productCode)
  if (tokens.length) return tokens.join(' · ')
  const raw = (line.productCode ?? '').trim()
  if (raw && raw !== '—') return raw
  return raw || '—'
}

/** 详情弹窗顶部彩色码 token，与列表卡片主行一致 */
export function inspectionSummaryTokensForOrderCard(
  order: OrderCardDTO,
  line: OrderServiceLineDTO,
): string[] {
  return productCodeTagPartsForOrderCard(mainLineInspectionProductCodeDisplay(order, line))
}

/** 详情弹窗彩色码：卡片码 + 有 Baseline 检查项时追加 B-n */
export function inspectionSummaryTokensForOrderDetail(
  order: OrderCardDTO,
  line: OrderServiceLineDTO,
  items?: CareOrderListItemChecklist[],
): string[] {
  const tokens = inspectionSummaryTokensForOrderCard(order, line)
  const b = countBaselineTaggedChecklistItems(items)
  return appendBaselineSummaryToken(tokens, b)
}

/** 与 `HMCareOrders.vue` `tagToneClass` 一致；`classPrefix` 列表用 `svc-tag`，详情弹窗用 `cod` */
export function productCodeTagToneClass(token: string, classPrefix = 'svc-tag'): string {
  const t = token.trim()
  if (t.startsWith('IC-')) return `${classPrefix}-ic`
  if (t.startsWith('I-')) return `${classPrefix}-i`
  if (t.startsWith('N-')) return `${classPrefix}-n`
  if (t.startsWith('A-')) return `${classPrefix}-a`
  if (t.startsWith('U-')) return `${classPrefix}-u`
  if (t.startsWith('B-')) return `${classPrefix}-b`
  if (t.startsWith('P-')) return `${classPrefix}-p`
  if (t.startsWith('F-')) return `${classPrefix}-f`
  return `${classPrefix}-plain`
}
